import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetParticipatingBrands } from '@/query/queries/investing/useGetParticipatingBrands';
import { useWindowSize } from '@/hooks/useWindowSize';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SliderNavigation } from './navigation';
import { BrandCard } from './card';
import IconEmpty from './assets/empty-state-image.svg';
import s from './index.module.scss';

const CARD_WIDTH = 332;
const SLIDE_GAP = 24;
const TABLET_BREAKPOINT = 768;
const THREE_CARDS_WIDTH = CARD_WIDTH * 3 + SLIDE_GAP * 2;

export const BrandsParticipation = () => {
  const { t } = useTranslation('investing');
  const { data, fetchNextPage, hasNextPage } = useGetParticipatingBrands();
  const { innerWidth } = useWindowSize();
  const [ready, setReady] = useState(false);
  const [swiperRef, setSwiperRef] = useState(null);
  const [params, setParams] = useState({
    isBeginning: true,
    isEnd: false,
  });

  const slidesPerView =
    innerWidth >= THREE_CARDS_WIDTH
      ? 3
      : innerWidth >= TABLET_BREAKPOINT
        ? 2
        : 1;

  useEffect(() => {
    if (ready && data?.pages.length) {
      setParams({
        isBeginning: true,
        isEnd: !!swiperRef?.isEnd,
      });
    }
  }, [ready, data?.pages.length, swiperRef?.isEnd]);

  const onNext = () => {
    swiperRef?.slideNext();
  };

  const onPrev = () => {
    swiperRef?.slidePrev();
  };

  if (!data) return null;

  const totalCount = data.pages[0]?.total_count || 0;
  const hasBrands = totalCount > 0;

  return (
    <div className={s.wrapper}>
      <div className={s.header}>
        <div className={s.title}>
          {`${t('investing:brand_participation.invest_brands')} ${hasBrands ? `(${totalCount})` : ''}`}
        </div>
        {totalCount > slidesPerView && (
          <div className={s.controls}>
            <SliderNavigation onNext={onNext} onPrev={onPrev} {...params} />
          </div>
        )}
      </div>

      {hasBrands ? (
        <div className={s.container}>
          <Swiper
            onAfterInit={() => setReady(true)}
            onSwiper={setSwiperRef}
            spaceBetween={SLIDE_GAP}
            slidesPerView={slidesPerView}
            onSlideChange={swiper => {
              setParams({
                isBeginning: swiper.isBeginning,
                isEnd:
                  swiper.isEnd || swiper.snapIndex - swiper.activeIndex === 1,
              });
            }}
            onReachEnd={() => {
              if (hasNextPage) {
                fetchNextPage();
              }
            }}
          >
            {data.pages.map(page =>
              page.list.map(brand => (
                <SwiperSlide key={brand.id}>
                  <BrandCard key={brand.id} brand={brand} />
                </SwiperSlide>
              )),
            )}
          </Swiper>
        </div>
      ) : (
        <div className={s.empty}>
          <div className={s.empty_content}>
            <img src={IconEmpty} alt="empty" width={128} height={128} />
            <div className={s.empty_title}>
              {t('investing:brand_participation.brands_prepared')}
            </div>
            <div className={s.empty_desc}>
              {t('investing:brand_participation.brands_upcoming')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
